import React, { useState, type ChangeEvent, useEffect } from 'react';
import styled from 'styled-components';
import { type AlertColor, SvgIcon } from '@mui/material';
import { Autorenew, Download, DeleteOutline } from '@mui/icons-material';
import type { FileData, IconFunctions } from './types';
import PDFIcon from './assets/file-pdf.svg';
import { downloadFile } from '../../utils';
import { Loading } from '../Loading';

type FileBoxProps = {
  file: FileData;
  index: number;
  fileExtension: 'pdf' | 'jpg';
  disabled?: boolean;
  removeFile: (fileId: string, index: number) => void;
  reUpload?: (e: ChangeEvent<HTMLInputElement>) => Promise<void>;
  fileInputRef?: React.RefObject<HTMLInputElement>;
  handleDisplayAlert: (alertType: AlertColor, alertContent: string) => void;
};

export const generateIcons = (
  file: FileData,
  fileExtension: 'pdf' | 'jpg',
  removeFile: (fileId: string, index: number) => void,
  index: number,
  disabled: boolean,
  handleDisplayAlert: (alertType: AlertColor, alertContent: string) => void,
  handleLoading: (callback: () => Promise<void>) => Promise<void>,
  handleUploadClick?: () => void,
): IconFunctions[] => [
  ...(fileExtension === 'pdf'
    ? []
    : [
        {
          component: Autorenew,
          function: () => {
            if (handleUploadClick) handleUploadClick();
          },
        },
      ]),

  {
    component: Download,
    function: async () => {
      await handleLoading(async () => {
        try {
          await downloadFile(file.id, fileExtension, file.name);
        } catch (error: any) {
          handleDisplayAlert(
            'error',
            'Error downloading file' + error?.message,
          );
        }
      });
    },
  },
  ...(disabled
    ? []
    : [
        {
          component: DeleteOutline,
          function: () => {
            removeFile(file.id, index);
          },
        },
      ]),
];

export const FileBox: React.FunctionComponent<
  FileBoxProps & {
    handleLoading: (callback: () => Promise<void>) => Promise<void>;
  }
> = props => {
  const [showMask, setShowMask] = useState(false);
  const {
    index,
    file,
    disabled = false,
    removeFile,
    fileExtension,
    handleDisplayAlert,
    handleLoading,
  } = props;

  const icons = generateIcons(
    file,
    fileExtension,
    removeFile,
    index,
    disabled,
    handleDisplayAlert,
    handleLoading,
  );

  return (
    <FileContainer
      onMouseOver={() => {
        setShowMask(true);
      }}
      onMouseOut={() => {
        setShowMask(false);
      }}
    >
      <div
        title={file.name}
        className="file-mask"
        style={{ display: showMask ? 'flex' : 'none' }}
      >
        {icons.map((icon, index) => {
          return (
            <SvgIcon
              key={index}
              component={icon.component}
              fontSize="small"
              inheritViewBox
              onClick={icon.function}
            />
          );
        })}
      </div>
      <img className="pdf-icon" src={PDFIcon} />
    </FileContainer>
  );
};

export const PhotoPanelBox: React.FunctionComponent<FileBoxProps> = (
  props: FileBoxProps,
) => {
  const [showMask, setShowMask] = useState(false);
  const [imageSrc, setImageSrc] = useState('');
  const [pageLoading, setPageLoading] = useState<boolean>(false);

  const {
    file,
    disabled = false,
    removeFile,
    fileExtension,
    reUpload,
    fileInputRef,
    handleDisplayAlert,
  } = props;

  const handleUploadClick = (): void => {
    if (fileInputRef?.current) {
      fileInputRef?.current.click();
    }
  };
  const handleLoading = async (
    callback: () => Promise<void>,
  ): Promise<void> => {
    setPageLoading(true);
    try {
      await callback();
    } finally {
      setPageLoading(false);
    }
  };

  const handleReUpload = async (
    e: ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    if (reUpload) {
      await reUpload(e);
    }
  };

  const icons = generateIcons(
    file,
    fileExtension,
    removeFile,
    0,
    disabled,
    handleDisplayAlert,
    handleLoading,
    handleUploadClick,
  );

  const handleMouseOver = (): void => {
    if (disabled) return;
    setShowMask(true);
  };
  const handleMouseOut = (): void => {
    if (disabled) return;
    setShowMask(false);
  };

  const loadImageWithHeader = async (file: FileData): Promise<void> => {
    const headers = new Headers();
    await handleLoading(async () => {
      try {
        const response = await fetch(`/files/${file.id}`, {
          method: 'GET',
          headers: {
            ...headers,
            Authorization: localStorage.getItem('tokens') ?? '',
            'current-roles': localStorage.getItem('currentRole') ?? '',
          },
        });
        if (!response.ok) {
          throw new Error('Failed to fetch the file');
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setImageSrc(url);
      } catch (error) {
        handleDisplayAlert(
          'error',
          `Error Occurs.` + (error as Error)?.message,
        );
      }
    });
  };

  useEffect(() => {
    void loadImageWithHeader(file);
  }, [file]);

  return (
    <PhotoPanelContainer
      onMouseOver={handleMouseOver}
      onMouseOut={handleMouseOut}
    >
      {pageLoading && <Loading />}

      <div
        title={file.name}
        className="file-mask"
        style={{ display: showMask ? 'flex' : 'none' }}
      >
        {icons.map((icon, index) => {
          return (
            <SvgIcon
              key={index}
              component={icon.component}
              fontSize="small"
              inheritViewBox
              onClick={icon.function}
            />
          );
        })}
      </div>
      <input
        type="file"
        style={{ display: 'none' }}
        ref={fileInputRef}
        onChange={handleReUpload}
        accept=".jpg"
      />

      {imageSrc && <img src={imageSrc} />}
    </PhotoPanelContainer>
  );
};

const PhotoPanelContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 0.5rem;
  padding: 0.25rem;
  width: 12rem;
  height: 12rem;
  position: relative;

  img {
    height: 100%;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 0.5rem;
    object-fit: cover; // to keep the proportions of the image while filling the container.
  }

  .file-mask {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.25);
    width: 12rem;
    height: 12rem;
    border-radius: 0.5rem;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  svg {
    cursor: pointer;
    color: #ffffff;
  }
`;

const FileContainer = styled.div`
  width: 5rem;
  height: 5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 0.5rem;
  margin: 0.5rem 0.2rem;

  .file-mask {
    position: absolute;
    background-color: rgba(0, 0, 0, 0.5);
    width: 5rem;
    height: 5rem;
    border-radius: 0.5rem;
    flex-direction: row;
    justify-content: center;
    align-items: center;
  }

  svg {
    cursor: pointer;
    color: #ffffff;
  }
`;
